import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Check,
  Copy,
  Download,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Logo } from '../../components/Logo';
import { useBrand } from '../../brand/BrandProvider';
import { cn } from '../../lib/cn';

const SECRET = 'JBSWY3DPEHPK3PXP';
const BACKUP_CODES = [
  'h47s-29kr',
  'v82l-xkkd',
  '2pn1-8wq3',
  'k8mz-fjdq',
  'm11p-zzst',
  'r4hd-bwlc',
  'q6mn-2eai',
  'x4dt-vp08',
];

/**
 * MFA setup — §7.2
 * Three blocks on one page: scan QR, enter code, save backup codes.
 */
export function MfaSetup() {
  const brand = useBrand();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  function verify() {
    if (code.length !== 6) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setVerified(true);
    }, 700);
  }

  function copySecret() {
    navigator.clipboard?.writeText(SECRET).catch(() => {});
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 1500);
  }

  function downloadCodes() {
    const blob = new Blob([BACKUP_CODES.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'possignin-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    setDownloaded(true);
  }

  function finish() {
    navigate({ to: '/' });
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark">
      <header className="px-4 sm:px-8 py-5 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center gap-2.5">
          <Logo />
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            {brand.name}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/profile"
            className="text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
          >
            Skip for now
          </Link>
          <AuthThemeToggle />
        </div>
      </header>

      <main className="px-4 sm:px-6 py-8 sm:py-12 max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-12 h-12 mx-auto mb-4 grid place-items-center rounded-xl bg-brand text-white dark:bg-brand-dm dark:text-zinc-950">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Set up two-factor auth
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 max-w-md mx-auto">
            Add a second layer of security. You'll need an authenticator app like{' '}
            <span className="font-medium text-zinc-800 dark:text-zinc-200">Authy</span> or{' '}
            <span className="font-medium text-zinc-800 dark:text-zinc-200">
              Google Authenticator
            </span>
            .
          </p>
        </div>

        {/* Step 1 — Scan */}
        <Card className="p-5 sm:p-7 mb-4">
          <div className="flex items-start gap-3 mb-5">
            <span className="w-7 h-7 grid place-items-center rounded-full bg-brand text-white dark:bg-brand-dm dark:text-zinc-950 text-sm font-semibold flex-shrink-0">
              1
            </span>
            <div>
              <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                Scan this QR code
              </h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                Point your authenticator app at the code below.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-7 items-center">
            <QrPlaceholder />

            <div className="space-y-3">
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1.5">
                  Or enter manually
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 mono text-sm text-zinc-800 dark:text-zinc-100 break-all">
                    {SECRET}
                  </code>
                  <button
                    type="button"
                    onClick={copySecret}
                    className="w-9 h-9 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition flex-shrink-0"
                    title="Copy"
                  >
                    {copiedSecret ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="flex items-start gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                <Smartphone className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Add account → "{brand.name} · imran@khan-bakery.com" → enter code below.
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Step 2 — Enter code */}
        <Card className="p-5 sm:p-7 mb-4">
          <div className="flex items-start gap-3 mb-5">
            <span
              className={cn(
                'w-7 h-7 grid place-items-center rounded-full text-sm font-semibold flex-shrink-0',
                verified
                  ? 'bg-emerald-600 text-white'
                  : 'bg-brand text-white dark:bg-brand-dm dark:text-zinc-950',
              )}
            >
              {verified ? <Check className="w-4 h-4" /> : '2'}
            </span>
            <div>
              <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                Enter the 6-digit code
              </h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                The code refreshes every 30 seconds in your app.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 max-w-sm">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              disabled={verified}
              placeholder="000000"
              className={cn(
                'flex-1 px-4 py-3 rounded-md text-center font-mono text-2xl tracking-[0.5em] tabular-nums',
                'bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50',
                'border border-zinc-300 dark:border-zinc-700',
                'focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm',
                verified && 'border-emerald-500 dark:border-emerald-600',
              )}
              autoComplete="one-time-code"
            />
            {verified ? (
              <span className="px-3 py-2 rounded-md text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" /> Verified
              </span>
            ) : (
              <Button
                type="button"
                onClick={verify}
                loading={submitting}
                disabled={code.length !== 6}
              >
                Verify
              </Button>
            )}
          </div>
        </Card>

        {/* Step 3 — Backup codes */}
        <Card className="p-5 sm:p-7 mb-4">
          <div className="flex items-start gap-3 mb-5">
            <span className="w-7 h-7 grid place-items-center rounded-full bg-brand text-white dark:bg-brand-dm dark:text-zinc-950 text-sm font-semibold flex-shrink-0">
              3
            </span>
            <div>
              <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                Save your backup codes
              </h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                Each code works once. Save them somewhere safe — they let you back in if you
                lose your phone.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
            {BACKUP_CODES.map((c) => (
              <code
                key={c}
                className="px-3 py-2 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 mono text-sm text-zinc-800 dark:text-zinc-100 text-center tabular-nums"
              >
                {c}
              </code>
            ))}
          </div>

          <Button
            variant="secondary"
            type="button"
            iconLeft={<Download className="w-4 h-4" />}
            onClick={downloadCodes}
          >
            {downloaded ? 'Downloaded' : 'Download codes (.txt)'}
          </Button>
        </Card>

        <div className="flex items-center justify-between gap-3 pt-2">
          <Link
            to="/profile"
            className="inline-flex items-center gap-1 text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Skip
          </Link>
          <Button onClick={finish} disabled={!verified}>
            {verified ? 'Finish setup' : 'Verify code first'}
          </Button>
        </div>
      </main>
    </div>
  );
}

/**
 * Stylized QR placeholder — gridded SVG box that *looks like* a QR code
 * without actually being one. Replace with the real QR when wired to the API.
 */
function QrPlaceholder() {
  // Pseudo-random but deterministic block pattern
  const cells = Array.from({ length: 25 }, (_, r) =>
    Array.from({ length: 25 }, (_, c) => (r * 7 + c * 13) % 3 === 0),
  );
  return (
    <div className="w-44 h-44 sm:w-48 sm:h-48 p-3 rounded-xl bg-white dark:bg-zinc-50 shadow-sm border border-zinc-200 dark:border-zinc-300 mx-auto sm:mx-0">
      <svg viewBox="0 0 25 25" className="w-full h-full" aria-label="2FA QR code">
        {/* finder squares */}
        {[
          [0, 0],
          [18, 0],
          [0, 18],
        ].map(([x, y], idx) => (
          <g key={idx} transform={`translate(${x},${y})`}>
            <rect width="7" height="7" fill="#09090B" />
            <rect x="1" y="1" width="5" height="5" fill="#FFFFFF" />
            <rect x="2" y="2" width="3" height="3" fill="#09090B" />
          </g>
        ))}
        {/* random data cells */}
        {cells.flatMap((row, r) =>
          row.map(
            (on, c) =>
              on &&
              !(r < 7 && c < 7) &&
              !(r < 7 && c >= 18) &&
              !(r >= 18 && c < 7) && (
                <rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" fill="#09090B" />
              ),
          ),
        )}
      </svg>
    </div>
  );
}
